import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import Calendar from "./calendar";
import moment from "moment";
import { ASIGNACION_FROM, VERSION_ASIGNACION } from "../page/asignacion/const/version";
import { dividirProductos } from "../api/api-asignacion";

/**
 * Stable unique key per row (idDetallePedido + position fallback so no collisions).
 * @param {object} line
 * @param {number} groupIndex
 * @param {number} lineIndex
 * @returns {string}
 */
function lineItemKey(line, groupIndex, lineIndex) {
    if (line == null || typeof line !== "object") return `x-${groupIndex}-${lineIndex}`;
    const id = line.idDetallePedido ?? line.idDetallepedido;
    if (id != null && String(id).trim() !== "") {
        return `${String(id)}#${groupIndex}#${lineIndex}`;
    }
    const idP = line.idProducto != null ? String(line.idProducto) : "";
    const idPed = line.idPedido != null ? String(line.idPedido) : "";
    const nDoc = line.numeroDocumento != null ? String(line.numeroDocumento) : "";
    return `${idP}::${idPed}::${nDoc}::g${groupIndex}::l${lineIndex}`;
}

/**
 * @param {object} row
 * @returns {string}
 */
function buildClienteGroupKey(row) {
    if (row == null || typeof row !== "object") return "";
    const id = row.idCliente != null ? String(row.idCliente).trim() : "";
    if (id !== "") return id;
    const cod = row.codigoCliente != null ? String(row.codigoCliente).trim() : "";
    return cod;
}

/**
 * @param {unknown} data
 * @returns {Array<{ key: string, idCliente: string, codigoCliente: string, nombreCliente: string, items: object[] }>}
 */
function groupLineItemsByCliente(data) {
    if (!Array.isArray(data) || data.length === 0) return [];
    const map = new Map();
    for (const row of data) {
        if (row == null || typeof row !== "object") continue;
        const key = buildClienteGroupKey(row);
        if (key === "") continue;
        if (!map.has(key)) {
            map.set(key, {
                key,
                idCliente: row.idCliente != null ? String(row.idCliente) : "",
                codigoCliente: row.codigoCliente != null ? String(row.codigoCliente) : "",
                nombreCliente: row.nombreCliente != null ? String(row.nombreCliente) : "",
                items: [],
            });
        }
        map.get(key).items.push(row);
    }
    return Array.from(map.values()).sort((a, b) => {
        const c = a.codigoCliente.localeCompare(b.codigoCliente, undefined, { numeric: true });
        if (c !== 0) return c;
        return a.nombreCliente.localeCompare(b.nombreCliente, undefined, { sensitivity: "base" });
    });
}

/**
 * All product lines start **unchecked** so the user opts in line by line.
 * @param {unknown} data
 * @returns {Record<string, boolean>}
 */
function initialLineSelectionMap(data) {
    const out = {};
    if (!Array.isArray(data) || data.length === 0) return out;
    const groups = groupLineItemsByCliente(data);
    groups.forEach((g, gi) => {
        g.items.forEach((line, li) => {
            const k = lineItemKey(line, gi, li);
            out[k] = false;
        });
    });
    return out;
}

/**
 * Total weight for a line: `peso` (unit) × `cantidad`. If `cantidad` is missing, uses `peso` alone.
 * @param {object} line
 * @returns {number}
 */
function lineWeightTotal(line) {
    if (line == null || typeof line !== "object") return 0;
    const peso = Number(line.peso);
    if (Number.isNaN(peso) || !isFinite(peso) || peso < 0) return 0;
    return peso ?? 0;
}

/**
 * Groups lines by idCliente + codigoCliente for display; **selection is per line** (checkbox on each product).
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {Array<object>|null} data
 * @param {(lineItems: object[]) => void} [onContinue]
 */
export default function DividirProductosModal({
    isOpen, onClose, data = null,
    listaVehiculos, vehiculoSelected, setVehiculoSelected,
    fechaSelected, setFechaSelected,
    listaFormulario, formularioSelected, setFormularioSelected,
    showToast, setProcessingVisible,
}) {
    const { t } = useTranslation();
    const modalRef = React.useRef(null);
    const [step, setStep] = useState("select");
    const [selectError, setSelectError] = useState("");
    const [selectedByLineKey, setSelectedByLineKey] = useState({});
    const [resultItems, setResultItems] = useState([]);
    const [numeroDocumentoGrupoByKey, setNumeroDocumentoGrupoByKey] = useState({});

    const groups = useMemo(() => groupLineItemsByCliente(data), [data]);
    const resultGroups = useMemo(() => groupLineItemsByCliente(resultItems), [resultItems]);

    const selectedWeightTotal = useMemo(() => {
        if (step === "summary" && Array.isArray(resultItems) && resultItems.length > 0) {
            return resultItems.reduce((sum, line) => sum + lineWeightTotal(line), 0);
        }
        let total = 0;
        groups.forEach((g, gi) => {
            g.items.forEach((line, li) => {
                const k = lineItemKey(line, gi, li);
                if (selectedByLineKey[k]) {
                    total += lineWeightTotal(line);
                }
            });
        });
        return total;
    }, [step, resultItems, groups, selectedByLineKey]);

    useEffect(() => {
        // console.log('data');
        // console.log(data);
        if (!isOpen) {
            setStep("select");
            setSelectError("");
            setResultItems([]);
            setSelectedByLineKey({});
            setNumeroDocumentoGrupoByKey({});
            return;
        }
        setStep("select");
        setSelectError("");
        setResultItems([]);
        setSelectedByLineKey(initialLineSelectionMap(data));
    }, [isOpen, data]);

    if (!isOpen) return null;

    const handleOutsideClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose();
        }
    };

    const toggleLine = (key) => {
        setSelectedByLineKey((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleContinuar = () => {
        const selected = [];
        groups.forEach((g, gi) => {
            g.items.forEach((line, li) => {
                const k = lineItemKey(line, gi, li);
                if (selectedByLineKey[k]) {
                    selected.push(line);
                }
            });
        });
        if (selected.length === 0) {
            setSelectError(t("label_sin_items_seleccionados"));
            return;
        }
        setSelectError("");
        const selectedGroups = groupLineItemsByCliente(selected);
        const initialNumeroDocumentoByKey = {};
        selectedGroups.forEach((g) => {
            const numeroDocumentoGrupo = g.items[0]?.numeroDocumento;
            const base = numeroDocumentoGrupo != null ? String(numeroDocumentoGrupo).trim() : "";
            initialNumeroDocumentoByKey[g.key] = {
                oldValue: base,
                newValue: base ? `${base}-1` : "",
            };
        });
        setNumeroDocumentoGrupoByKey(initialNumeroDocumentoByKey);
        setResultItems(selected);
        setStep("summary");
    };

    const handleVolver = () => {
        setStep("select");
    };

    const handleAceptarResumen = async () => {
        try {
            if (vehiculoSelected === null || fechaSelected === null || formularioSelected === null) {
                if (showToast) {
                    showToast(t("alert_debe_seleccionar_vehiculo_fecha_formulario"));
                }
                return;
            }
            if (moment(fechaSelected).isValid() === false) {
                if (showToast) {
                    showToast(t("alert_fecha_invalida"));
                }
                return;
            }
            // console.log("DividirProductosModal — productos seleccionados:", resultItems);
            let objEnviar = {};
            objEnviar.listaProductos = resultItems;
            objEnviar.valuePlaca = vehiculoSelected.value;
            objEnviar.labelPlaca = vehiculoSelected.label;
            objEnviar.fechaEntrega = fechaSelected;
            objEnviar.idFormulario = formularioSelected.value;
            objEnviar.numeroDocumentoGrupo = numeroDocumentoGrupoByKey;
            objEnviar.version = VERSION_ASIGNACION;
            objEnviar.from = ASIGNACION_FROM.DIVIDIR_ENTREGAS;
            // console.log('objEnviar');
            // console.log(objEnviar);
            // return;
            setProcessingVisible(true);
            const response = await dividirProductos(objEnviar);
            setProcessingVisible(false);
            if (response.error === false) {
                showToast(t("alert_se_dividio_correctamente"));
                onClose();
                window.location.reload();
                return;
            }
            if (response.error === true) {
                showToast(response.message);
            }
        } catch (error) {
            console.log('handleAceptarResumen error');
            console.log(error);
        } finally {
        }

    };

    const formatMonto = (v) => {
        const n = Number(v);
        if (Number.isNaN(n)) return "—";
        return n.toFixed(2);
    };

    const renderSelect = () => {
        try {
            return (
                <div className="space-y-3">
                    {!Array.isArray(data) || data.length === 0 || groups.length === 0 ? (
                        <p className="text-gray-500 text-sm m-0 leading-normal">
                            {t("label_no_hay_informacion_disponible")}
                        </p>
                    ) : (
                        <>
                            {selectError ? (
                                <p className="text-sm text-amber-700 m-0" role="alert">
                                    {selectError}
                                </p>
                            ) : null}
                            <div className="space-y-4">
                                {groups.map((g, gi) => {
                                    // console.log('g');
                                    // console.log(g);
                                    const numeroDocumento = g.items[0].numeroDocumento;
                                    return (
                                        <div
                                            key={g.key}
                                            className="border border-gray-200 rounded-lg overflow-hidden"
                                        >
                                            <div className="px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                                                <div className="text-sm font-semibold text-gray-900 min-w-0">
                                                    <span className="tabular-nums text-gray-700">
                                                        {g.codigoCliente}
                                                    </span>
                                                    <span className="mx-2 text-gray-400" aria-hidden>
                                                        ·
                                                    </span>
                                                    <span className="break-words">{g.nombreCliente}</span>
                                                    <span className="ml-2 text-gray-500 font-normal text-xs">
                                                        ({g.items.length} {t("label_productos")})
                                                    </span>
                                                    <span className="ml-2 text-gray-500 font-normal text-xs">{numeroDocumento}</span>
                                                </div>
                                            </div>
                                            <div className="border border-t-0 border-gray-200 rounded-b-lg">
                                                <table className="w-full max-w-full text-sm border-collapse table-fixed">
                                                    <colgroup>
                                                        <col style={{ width: "40px" }} />
                                                        <col style={{ width: "40%" }} />
                                                        <col style={{ width: "9%" }} />
                                                        <col style={{ width: "9%" }} />
                                                        <col style={{ width: "10%" }} />
                                                        <col style={{ width: "14%" }} />
                                                    </colgroup>
                                                    <thead>
                                                        <tr className="bg-white text-xs font-semibold text-gray-600 border-b border-gray-100">
                                                            <th
                                                                scope="col"
                                                                className="py-2 px-1 w-10"
                                                                aria-label=" "
                                                            />
                                                            <th
                                                                scope="col"
                                                                className="text-left py-2 px-2 font-semibold"
                                                            >
                                                                {t("column_nombre")}
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="text-right py-2 px-1 font-semibold"
                                                            >
                                                                {t("column_cantidad")}
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="text-left py-2 px-1 font-semibold"
                                                            >
                                                                {t("column_unidad_medida")}
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="text-right py-2 px-1 font-semibold"
                                                            >
                                                                {t("column_monto")}
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="text-left py-2 px-2 font-semibold"
                                                            >
                                                                {t("column_weight")}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {g.items.map((line, li) => {
                                                            const k = lineItemKey(line, gi, li);
                                                            const idCb = `dividir-line-${gi}-${li}`;
                                                            return (
                                                                <tr
                                                                    key={k}
                                                                    className="border-b border-gray-50 last:border-0"
                                                                >
                                                                    <td className="py-1.5 px-1 align-top text-center w-10">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="h-4 w-4 rounded border-gray-300"
                                                                            checked={!!selectedByLineKey[k]}
                                                                            onChange={() => toggleLine(k)}
                                                                            id={idCb}
                                                                            aria-label={
                                                                                line.nombreProducto
                                                                                    ? String(line.nombreProducto)
                                                                                    : k
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td className="py-1.5 px-2 text-gray-900 align-top break-words leading-normal">
                                                                        <label
                                                                            htmlFor={idCb}
                                                                            className="cursor-pointer"
                                                                        >
                                                                            {line.nombreProducto}
                                                                        </label>
                                                                    </td>
                                                                    <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
                                                                        {line.cantidad}
                                                                    </td>
                                                                    <td className="py-1.5 px-1 text-gray-700">
                                                                        {line.unidadMedida}
                                                                    </td>
                                                                    <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
                                                                        {formatMonto(line.monto)}
                                                                    </td>
                                                                    <td className="py-1.5 px-2 text-gray-700 tabular-nums">
                                                                        {line.peso ?? 0}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            );
        } catch (error) {
            console.log('renderSelect error');
            console.log(error);
            return null;
        }
    };

    const renderSummary = () => {
        console.log('resultItems');
        console.log(resultItems);
        try {
            return (
                <div className="space-y-3">
                    <h3 className="text-base font-semibold text-gray-900 m-0">
                        {t("label_items_seleccionados")} ({resultItems.length})
                    </h3>
                    {resultItems.length === 0 ? (
                        <p className="text-gray-500 text-sm m-0">
                            {t("label_no_hay_informacion_disponible")}
                        </p>
                    ) : (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-4 lg:col-span-4 xl:col-span-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t("label_vehiculo")}<span className="text-red-500">*</span></label>
                                    <Select
                                        options={listaVehiculos}
                                        value={vehiculoSelected}
                                        onChange={(option) => setVehiculoSelected(option)}
                                        placeholder={t("placeholder_seleccione_vehiculo")}
                                        isClearable={true}
                                        isSearchable={true}
                                        classNamePrefix="react-select"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 999 }),
                                            menu: (base) => ({ ...base, zIndex: 999 }),
                                        }}
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-4 lg:col-span-4 xl:col-span-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t("label_fecha_entrega")}<span className="text-red-500">*</span></label>
                                    <Calendar
                                        value={fechaSelected}
                                        onChange={(date) => setFechaSelected(date)}
                                        placeholder={t("placeholder_seleccione_fecha")}
                                        id="fecha_asignacion"
                                        name="fecha_asignacion"
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-4 lg:col-span-4 xl:col-span-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t("label_formulario")}<span className="text-red-500">*</span></label>
                                    <Select
                                        options={listaFormulario}
                                        value={formularioSelected}
                                        onChange={(option) => setFormularioSelected(option)}
                                        placeholder={t("placeholder_seleccione_formulario")}
                                        isClearable={true}
                                        isSearchable={true}
                                        classNamePrefix="react-select"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 999 }),
                                            menu: (base) => ({ ...base, zIndex: 999 }),
                                        }}
                                    />
                                </div>
                            </div>
                            <br />
                            <div className="space-y-4">
                                {resultGroups.map((g, gi) => {
                                    const numeroDocumentoGrupo = g.items[0]?.numeroDocumento;
                                    return (
                                        <div
                                            key={`summary-${g.key}`}
                                            className="border border-gray-200 rounded-lg overflow-hidden"
                                        >
                                            <div className="px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                                                <div className="text-sm font-semibold text-gray-900 min-w-0">
                                                    <span className="tabular-nums text-gray-700">
                                                        {g.codigoCliente}
                                                    </span>
                                                    <span className="mx-2 text-gray-400" aria-hidden>
                                                        ·
                                                    </span>
                                                    <span className="break-words">{g.nombreCliente}</span>
                                                    <span className="ml-2 text-gray-500 font-normal text-xs">
                                                        ({g.items.length} {t("label_productos")})
                                                    </span>
                                                    {numeroDocumentoGrupo ? (
                                                        <span className="ml-2 text-gray-500 font-normal text-xs">
                                                            {numeroDocumentoGrupo}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className="border border-t-0 border-gray-200 rounded-b-lg">
                                                <table className="w-full max-w-full text-sm border-collapse table-fixed">
                                                    <colgroup>
                                                        <col style={{ width: "52%" }} />
                                                        <col style={{ width: "12%" }} />
                                                        <col style={{ width: "12%" }} />
                                                        <col style={{ width: "12%" }} />
                                                        <col style={{ width: "12%" }} />
                                                    </colgroup>
                                                    <thead>
                                                        <tr className="bg-white text-xs font-semibold text-gray-600 border-b border-gray-100">
                                                            <th scope="col" className="text-left py-2 px-2">
                                                                {t("column_nombre")}
                                                            </th>
                                                            <th scope="col" className="text-right py-2 px-1">
                                                                {t("column_cantidad")}
                                                            </th>
                                                            <th scope="col" className="text-left py-2 px-1">
                                                                {t("column_unidad_medida")}
                                                            </th>
                                                            <th scope="col" className="text-right py-2 px-1">
                                                                {t("column_monto")}
                                                            </th>
                                                            <th scope="col" className="text-left py-2 px-2">
                                                                {t("column_weight")}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr className="bg-gray-50 border-b border-gray-100">
                                                            <td colSpan={5} className="py-2 px-2">
                                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                    {t("label_numero_documento")}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                                    placeholder={t("placeholder_ingrese_numero_documento")}
                                                                    maxLength={24}
                                                                    value={numeroDocumentoGrupoByKey[g.key]?.newValue ?? ""}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setNumeroDocumentoGrupoByKey((prev) => ({
                                                                            ...prev,
                                                                            [g.key]: {
                                                                                ...(prev[g.key] || { oldValue: "", newValue: "" }),
                                                                                newValue: value,
                                                                            },
                                                                        }));
                                                                    }}
                                                                />
                                                                <p className="text-xs text-gray-500 mt-1 flex justify-end">
                                                                    {(numeroDocumentoGrupoByKey[g.key]?.newValue ?? "").length}/24
                                                                </p>
                                                            </td>
                                                        </tr>
                                                        {g.items.map((line, li) => {
                                                            const rkey = `${lineItemKey(line, gi, li)}-r${li}`;
                                                            return (
                                                                <tr
                                                                    key={rkey}
                                                                    className="border-b border-gray-50 last:border-0"
                                                                >
                                                                    <td className="py-1.5 px-2 text-gray-900 break-words leading-normal">
                                                                        {line.nombreProducto}
                                                                    </td>
                                                                    <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
                                                                        {line.cantidad}
                                                                    </td>
                                                                    <td className="py-1.5 px-1 text-gray-700">
                                                                        {line.unidadMedida}
                                                                    </td>
                                                                    <td className="py-1.5 px-1 text-right tabular-nums text-gray-800">
                                                                        {formatMonto(line.monto)}
                                                                    </td>
                                                                    <td className="py-1.5 px-2 text-gray-700 tabular-nums">
                                                                        {line.peso ?? 0}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        } catch (error) {
            console.log('renderSummary error');
            console.log(error);
            return null;
        }
    };

    return (
        <div
            className="fixed z-[60] inset-0 flex items-center justify-center p-4"
            onMouseDown={handleOutsideClick}
        >
            <div className="absolute inset-0 bg-black/10" />

            <div
                ref={modalRef}
                className="relative bg-white rounded-lg shadow-lg w-[calc(100%-1.5rem)] max-w-[8.5in] sm:w-[calc(100%-2.5rem)] max-h-[90vh] flex flex-col"
            >
                <div className="p-6 border-b flex flex-wrap justify-between items-center gap-3">
                    <h2 className="text-lg font-semibold m-0 min-w-0">
                        {t("label_dividir")} — {t("label_productos")}
                    </h2>
                    <div className="text-sm sm:text-base font-semibold text-gray-800 tabular-nums shrink-0">
                        {t("label_peso")}: {selectedWeightTotal.toFixed(2)} kg
                    </div>
                </div>

                <div
                    className="bg-white text-black p-6 overflow-y-auto"
                    style={{ fontFamily: "sans-serif" }}
                >
                    {step === "select" && (
                        renderSelect()
                    )}

                    {step === "summary" && (
                        renderSummary()
                    )}
                </div>

                <div className="p-6 border-t flex justify-end gap-2 flex-wrap">
                    {step === "summary" ? (
                        <>
                            <button
                                type="button"
                                onClick={handleVolver}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                            >
                                {t("label_volver")}
                            </button>
                            <button
                                type="button"
                                onClick={handleAceptarResumen}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                            >
                                {t("label_aceptar")}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                            >
                                {t("label_cerrar")}
                            </button>
                            {groups.length > 0 ? (
                                <button
                                    type="button"
                                    onClick={handleContinuar}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                                >
                                    {t("label_continuar")}
                                </button>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
